import { Spi as SpiClient, TransactionOptions } from '@mx51/spi-client-js';

import { ApiModel }                           from '../../app-lite/api/models/api.model';
import { ApiService }                         from '../../app-lite/api/api.service';
import { AssemblyPaymentStatusesModel }       from './models/assembly-payment.statuses';
import { AssemblyPaymentsUnpairOptionsModel } from './models/assembly-payments.unpair-options';
import { AssemblyPaymentTerminalModel }       from './models/assembly-payment.terminal';
import { CompanyIntegrationsModel }           from '../../app-lite/companies/models/company.integrations';
import { ConfirmService }                     from '../../app-lite/messages/confirm/confirm.service';
import { CustomerPaymentModel }               from '../../app-lite/customer-payments/models/customer-payment';
import { InvoiceModel }                       from '../../app-lite/invoices/models/invoice';
import { JsonService }                        from '../../app-lite/core/services/json.service';
import { TableQueryParameters }               from '../../app-lite/table/types/table-query-parameters';
import { TableService }                       from '../../app-lite/table/table.service';

import AssemblyPaymentsPairHtml        from './assembly-payments.pair.html';
import AssemblyPaymentsTransactionHtml from '../../app-lite/assembly-payments/assembly-payments.transaction.html';

export class AssemblyPaymentsService {
  $q            : ng.IQService;
  $rootScope    : any;
  $stateParams  : ng.ui.IStateParamsService;
  $translate    : ng.translate.ITranslateService;
  $uibModal     : ng.ui.bootstrap.IModalService;
  $window       : ng.IWindowService;
  api           : ApiService;
  API           : ApiModel;
  confirm       : ConfirmService;
  receiptOptions: TransactionOptions;
  spiApi        : SpiClient;
  STATUSES      : AssemblyPaymentStatusesModel;
  tableApi      : TableService;

  apiKey         = '8XMeAfbitTZYqt637GGHyBKR3sGV04Fc';
  storageKey     = 'assemblyPaymentsTerminal';
  tableCacheKey  = 'assemblyPaymentTerminals';
  tenantId       = 'wbc';
  terminalsRoute = '/pos_terminals/assembly_payments_terminal_list';
  vendorKey      = 'workshopsoftware';
  version        = '2.9';

  constructor (
    $q                       : ng.IQService,
    $rootScope               : any,
    $stateParams             : ng.ui.IStateParamsService,
    $translate               : ng.translate.ITranslateService,
    $uibModal                : ng.ui.bootstrap.IModalService,
    $window                  : ng.IWindowService,
    API                      : ApiModel,
    ASSEMBLY_PAYMENT_STATUSES: AssemblyPaymentStatusesModel,
    confirm                  : ConfirmService,
    private jsonApi          : JsonService,
    tableApi                 : TableService,
    workshopAPI              : ApiService
  ) {
    this.$q           = $q;
    this.$rootScope   = $rootScope;
    this.$stateParams = $stateParams;
    this.$translate   = $translate;
    this.$uibModal    = $uibModal;
    this.$window      = $window;
    this.api          = workshopAPI;
    this.API          = API;
    this.confirm      = confirm;
    this.STATUSES     = ASSEMBLY_PAYMENT_STATUSES;
    this.tableApi     = tableApi;
  }

  activate ( terminal : AssemblyPaymentTerminalModel ) : ng.IPromise<boolean> {
    return this.confirm.generic(this.$translate.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', { name: this.getPosName(terminal) }))
    .then(() => this.api.get('/company_integration/add_assembly_payments'));
  }

  deactivate ( terminal : AssemblyPaymentTerminalModel ) : ng.IPromise<boolean> {
    this.removeTerminalFromStorage();

    return this.confirm.generic(this.$translate.instant('JS_SPACE.INTEGRATIONS.REMOVE', { name: this.getPosName(terminal) }))
    .then(() => this.api.get('/company_integration/remove_assembly_payments'));
  }

  deleteTerminal ( terminal : AssemblyPaymentTerminalModel ) : ng.IPromise<boolean> {
    return this.confirm.generic(this.$translate.instant('ASSEMBLY_PAYMENTS_SPACE.DELETE_PAIRING'))
    .then(() => this.api.delete('/pos_terminal', terminal.id))
    .then(() => {
      const currentTerminal = this.getTerminal();

      if (currentTerminal && currentTerminal.id === terminal.id) {
        return this.unpairTerminal(terminal, { skipConfirmation: true });
      }

      return true;
    });
  }

  getPairedTerminal () : ng.IPromise<AssemblyPaymentTerminalModel> {
    const terminal = this.getTerminal();

    if (!terminal) {
      return this.$q.reject('EFTPOS Payment type used but, terminal is not paired. Please pair terminal in settings.');
    }

    return this.isTerminalPaired(terminal) ?
      this.$q.when(terminal) :
      this.$q.reject('EFTPOS Payment type used but, terminal is currently not connecting to the machine. Check status.');
  }

  getPosName ( terminal : AssemblyPaymentTerminalModel ) : string {
    return this.$translate.instant('COMPANY_SPACE.INTEGRATIONS.ASSEMBLY_PAYMENTS.WESTPAC_GROUP');
  }

  getTerminalCount () : ng.IPromise<number> {
    return this.api.get('/pos_terminals/assembly_payments_terminal_list_count');
  }

  getTerminal () : AssemblyPaymentTerminalModel {
    return this.jsonApi.parse(localStorage.getItem(this.storageKey));
  }

  getTerminals ( params : TableQueryParameters = {} ) : ng.IPromise<Array<AssemblyPaymentTerminalModel>> {
    const cache = this.tableApi.getCacheForTable(this.tableCacheKey);

    return this.api.get(this.terminalsRoute, [
      params.offset           || 0,
      cache.paging.pageSize   || ( params.pageSize  || 5 ),
      cache.sorting.field     || ( params.field     || 'ap_pos_id' ),
      cache.sorting.direction || ( params.direction || 'asc')
    ])
    .then(( terminals : Array<AssemblyPaymentTerminalModel> ) => {
      const currentTerminal = this.getTerminal();

      terminals.map(( terminal : AssemblyPaymentTerminalModel ) => {
        terminal.current = currentTerminal && terminal.id === currentTerminal.id;
        terminal.paired  = terminal.current && this.isTerminalPaired(currentTerminal);
        terminal.status  = ( currentTerminal && terminal.current ) ? currentTerminal.status : '';

        return terminal;
      });

      return this.$q.when(terminals);
    });
  }

  init () : void {
    this.initializeSpi();
    this.setupListeners();
  }

  initializeSpi ( terminal : AssemblyPaymentTerminalModel = this.getTerminal() ) : void {
    if (terminal) {
      this.spiApi = new SpiClient(
        terminal.ap_pos_id,
        terminal.ap_serial_number,
        terminal.alternate_ip_address || terminal.ap_ip_address,
        terminal.secrets ? {
          EncKey : terminal.secrets.EncKey,
          HmacKey: terminal.secrets.HmacKey
        } : null
      );

      this.spiApi.Config.PromptForCustomerCopyOnEftpos = terminal.ap_print_receipt_from_pos;
      this.spiApi.Config.PrintMerchantCopy             = terminal.ap_print_receipt_from_pos;
      this.spiApi.Config.SignatureFlowOnEftpos         = terminal.ap_print_receipt_from_pos;

      this.spiApi.SetPosInfo(this.vendorKey, this.version);
      this.spiApi.SetTenantCode(this.tenantId);
      this.spiApi.SetDeviceApiKey(this.apiKey);
      this.spiApi.SetSecureWebSockets(true);
      this.spiApi.SetTestMode(this.$rootScope.Company.company.pos_terminal_settings.ap_use_test_mode);
      this.spiApi.SetAutoAddressResolution(true);

      this.overrideMethods();
      this.setReceiptOptions();

      try {
        this.spiApi.Start();
      } catch (error) {
        this.unpairTerminal(terminal, { skipConfirmation: true });
      }

      this.spiApi.AckFlowEndedAndBackToIdle();
    }
  }

  isTerminalPaired ( terminal ?: AssemblyPaymentTerminalModel ) : boolean {
    return terminal
      ? terminal.status === this.STATUSES.paired
      : ( this.spiApi
        ? this.spiApi.CurrentStatus === this.STATUSES.paired
        : false
      );
  }

  overrideMethods () : void {
    this.spiApi.BatteryLevelChanged = e => {
      console.log('Battery level changed', e);
    };

    this.spiApi.TerminalConfigurationResponse = e => {
      console.log('Terminal configuration response', e);

      this.spiApi.GetTerminalStatus();
    };

    this.spiApi.TerminalStatusResponse = e => {
      console.log('Terminal status response', e);
    };

    this.spiApi.TransactionUpdateMessage = e => {
      console.log('Transaction update', e);
    };
  }

  pairTerminal ( terminal : AssemblyPaymentTerminalModel ) : ng.IPromise<any> {
    return this.$uibModal.open({
      controller : 'AssemblyPaymentsPairCtrl',
      templateUrl: AssemblyPaymentsPairHtml,
      windowClass: 'md-modal',
      resolve    : {
        terminal: () => terminal
      }
    }).result;
  }

  processPayment ( payment : CustomerPaymentModel, invoice = {} as InvoiceModel, amount : number, isRefund = false ) : ng.IPromise<any> {
    let data;
    let terminal;

    return this.getPairedTerminal()
    .then(( response : AssemblyPaymentTerminalModel ) => {
      terminal = response;

      return this.$uibModal.open({
        controller  : 'AssemblyPaymentsTransactionCtrl',
        controllerAs: '$ctrl',
        templateUrl : AssemblyPaymentsTransactionHtml,
        windowClass : 'md-modal',
        resolve     : {
          amount () {
            const bdAmount = amount ? new BigDecimal(amount.toString()) : BigDecimal.ZERO;
            const hundred  = new BigDecimal('100');

            return bdAmount.multiply(hundred).setScale(0, BigDecimal.ROUND_HALF_DOWN).intValueExact();
          },
          invoice : () => invoice,
          isRefund: () => isRefund,
          payment : () => payment,
          terminal: () => terminal
        }
      }).result;
    })
    .then(result => {
      data = result;

      return data.complete
        ? ( isRefund
            ? this.api.get('/invoice/process_refund', invoice.id)
            : this.api.post('/customer_payments/process_assembly_payments_payment', {
              payment_id: payment.id
            }, 'customer_payment')
          )
          : ( data.payment_status === 'incorrect_pin'
            ? this.$q.reject('Incorrect Pin. Payment Cancelled.')
            : this.$q.reject('Signature Declined. Payment Cancelled.')
          );
    })
    .then(() => {
      return data
        ? this.api[data.receipt_id ? 'patch' : 'post']('/eft_receipt', {
          customer_receipt: data.customer_receipt,
          invoice_id      : invoice.id,
          merchant_receipt: data.merchant_receipt,
          payment_id      : payment.id,
          payment_status  : data.payment_status,
          payment_type    : invoice.cash_or_account === 'C' ? 'cash' : 'account',
          pos_terminal_id : terminal.id,
          receipt_id      : data.receipt_id
        }, 'eft_receipt')
        : this.$q.reject(this.$translate.instant('JS_SPACE.MESSAGES.CANCELED', {
          name: 'Payment'
        }));
    })
    .then(receipt => {
      if (terminal.ap_print_receipt_from_pos) {
        return this.$q.when(true);
      }

      if (data.complete) {
        this.$window.open(`${ this.API.url }/reports/jasper_eftpos_receipt/merchant/${ receipt.id }.pdf`, '_blank' );
      }

      this.confirm.generic(this.$translate.instant('JS_SPACE.CONFIRM.PRINT_COPY'))
      .then(() => {
        this.$window.open(`${ this.API.url }/reports/jasper_eftpos_receipt/customer/${ receipt.id }.pdf`, '_blank' );
      });

      return this.$q.when(receipt.id);
    });
  }

  removeTerminalFromStorage () : void {
    localStorage.removeItem(this.storageKey);
  }

  saveTerminal ( terminal : AssemblyPaymentTerminalModel ) : ng.IPromise<AssemblyPaymentTerminalModel> {
    terminal.alternate_ip_address = terminal.ap_ip_address;

    return terminal.id
      ? this.api.patch('/pos_terminal', terminal, 'pos_terminal')
      : this.api.post('/pos_terminal', terminal, 'pos_terminal');
  }

  saveTestMode ( integrations : CompanyIntegrationsModel ) : ng.IPromise<CompanyIntegrationsModel> {
    return this.api.patch('/company_integrations', integrations, 'company_integration');
  }

  setReceiptOptions () : void {
    this.receiptOptions = new TransactionOptions();

    this.receiptOptions.SetMerchantReceiptHeader('');
    this.receiptOptions.SetMerchantReceiptFooter('');
    this.receiptOptions.SetCustomerReceiptHeader('');
    this.receiptOptions.SetCustomerReceiptFooter('');
  }

  setupListeners () {
    document.addEventListener('SecretsChanged', ( e : CustomEvent ) => {
      const currentTerminal = this.getTerminal();

      if (!e.detail && currentTerminal) {
        this.api.get('/pos_terminal/clear_keys_from_assembly_payments', currentTerminal.id)
        .then(() => {
          this.removeTerminalFromStorage();

          this.$window.location.reload();
        });
      };
    });

    document.addEventListener('StatusChanged', ( e : CustomEvent ) => {
      const currentTerminal = this.getTerminal();

      if (currentTerminal) {
        if (currentTerminal.status !== e.detail) {
          currentTerminal.status = e.detail;

          this.storeTerminal(currentTerminal);
        };
      }
      else {
        const terminal = {
          alternate_ip_address: this.spiApi._eftposAddress,
          status              : e.detail
        };

        this.storeTerminal(terminal);
      };
    });
  }

  storeTerminal ( terminal : AssemblyPaymentTerminalModel ) : void {
    localStorage.setItem(this.storageKey, angular.toJson(terminal));
  }

  unpairTerminal ( terminal : AssemblyPaymentTerminalModel = this.getTerminal(), options : AssemblyPaymentsUnpairOptionsModel = {} ) : ng.IPromise<boolean> {
    const errorMessage = 'Cannot find terminal to unpair. Process canceled.';

    return this.$q.when()
    .then(() => {
      if ( !terminal || !this.spiApi ) {
        return this.$q.reject(errorMessage);
      }

      return options.skipConfirmation
        ? this.$q.when(true)
        : this.confirm.generic(this.$translate.instant('ASSEMBLY_PAYMENTS_SPACE.UNPAIR'));
    })
    .then(() => {
      this.spiApi.AckFlowEndedAndBackToIdle();

      return this.spiApi.Unpair();
    })
    .then(() => {
      if ( this.spiApi._currentStatus === 'Unpaired' ) {
        this.removeTerminalFromStorage();

        return this.$q.when(true);
      }

      return this.$q.reject(errorMessage);
    });
  }
}
